import React, { useEffect, useMemo, useRef, useState } from "react";
import { ActionIcon } from "@mantine/core";
import { useSelector, useDispatch } from "react-redux";
import ButtonWithPopoverFeedback from "../ButtonWithPopoverFeedback";
import { IconCopy, IconEye } from "@tabler/icons-react";
import { setTableSort } from "../../actions/tableSortingActions";
import { CELL_BORDER, FullValueModal, SortIcon } from "./DevTableUtils";

const CHAR_LIMIT = 1200;

// Sorting stringified numbers with localeCompare puts "20" before "100" -
// compare numerically when both sides parse cleanly as numbers, otherwise
// fall back to locale string comparison.
function compareSortValues(a, b) {
	if (a !== "" && b !== "") {
		const numA = Number(a);
		const numB = Number(b);
		if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
			return numA - numB;
		}
	}
	return a.localeCompare(b);
}

function TransposedRow({ field, value, truncate, onShowFull }) {
	const [hovered, setHovered] = useState(false);
	const textRef = useRef(null);
	const [isOverflowing, setIsOverflowing] = useState(false);

	const isNull = value === null || value === undefined;
	const strValue = isNull ? "null" : String(value);
	const isCharTruncated = strValue.length > CHAR_LIMIT;
	const displayValue = isCharTruncated
		? strValue.slice(0, CHAR_LIMIT) + "…"
		: strValue;

	useEffect(() => {
		if (truncate && textRef.current) {
			setIsOverflowing(
				textRef.current.scrollHeight > textRef.current.clientHeight + 1
			);
		} else {
			setIsOverflowing(false);
		}
	}, [truncate, strValue]);

	const showEye = hovered && (isOverflowing || isCharTruncated) && !isNull;
	const showCopy = hovered && !isNull;

	return (
		<tr
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
		>
			<td
				style={{
					padding: "3px 8px",
					color: "#555",
					fontWeight: 600,
					fontSize: 12,
					fontFamily: "monospace",
					whiteSpace: "nowrap",
					verticalAlign: "middle",
					width: "1px",
					border: CELL_BORDER,
					backgroundColor: "#f5f5f5",
				}}
			>
				{field}
			</td>
			<td
				style={{
					padding: "3px 8px",
					verticalAlign: "middle",
					position: "relative",
					border: CELL_BORDER,
				}}
			>
				<span
					ref={textRef}
					style={{
						display: truncate ? "-webkit-box" : "block",
						WebkitLineClamp: truncate ? 2 : undefined,
						WebkitBoxOrient: truncate ? "vertical" : undefined,
						overflow: truncate ? "hidden" : undefined,
						fontSize: 12,
						fontFamily: "monospace",
						color: isNull ? "#bbb" : "#222",
						fontStyle: isNull ? "italic" : undefined,
						wordBreak: "break-all",
					}}
				>
					{displayValue}
				</span>
				{showEye || showCopy ? (
					<div
						style={{
							position: "absolute",
							right: 2,
							top: "50%",
							transform: "translateY(-50%)",
							background: "white",
							display: "flex",
							gap: 2,
						}}
					>
						{showEye ? (
							<ActionIcon
								size="xs"
								variant="subtle"
								onClick={() =>
									onShowFull({
										value: strValue,
										label: field,
									})
								}
								title="Show full value"
							>
								<IconEye size={11} />
							</ActionIcon>
						) : null}
						{showCopy ? (
							<ButtonWithPopoverFeedback
								iconComponent={<IconCopy size={13} />}
								onClick={() =>
									navigator.clipboard.writeText(strValue)
								}
								feedbackMessage="Copied!"
								title="Copy"
							/>
						) : null}
					</div>
				) : null}
			</td>
		</tr>
	);
}

function CustomRendererTransposedRow({
	field,
	value,
	customRenderer,
	onShowFull,
}) {
	const [hovered, setHovered] = useState(false);

	const isNull = value === null || value === undefined;
	const strings = !isNull ? customRenderer.getStrings?.(value) : null;
	const strValue = strings?.rawStr ?? (isNull ? "null" : String(value));

	const showEye = hovered && !isNull;
	const showCopy = hovered && !isNull;

	return (
		<tr
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
		>
			<td
				style={{
					padding: "3px 8px",
					color: "#555",
					fontWeight: 600,
					fontSize: 12,
					fontFamily: "monospace",
					whiteSpace: "nowrap",
					verticalAlign: "middle",
					width: "1px",
					border: CELL_BORDER,
					backgroundColor: "#f5f5f5",
				}}
			>
				{field}
			</td>
			<td
				style={{
					padding: "3px 8px",
					verticalAlign: "middle",
					position: "relative",
					border: CELL_BORDER,
				}}
			>
				{customRenderer(value)}
				{showEye || showCopy ? (
					<div
						style={{
							position: "absolute",
							right: 2,
							top: "50%",
							transform: "translateY(-50%)",
							background: "white",
							display: "flex",
							gap: 2,
						}}
					>
						{showEye ? (
							<ActionIcon
								size="xs"
								variant="subtle"
								onClick={() =>
									onShowFull({
										value: strValue,
										friendlyValue:
											strings?.friendlyStr ?? null,
										friendlyLabel:
											strings?.friendlyLabel ?? null,
										label: field,
									})
								}
								title="Show full value"
							>
								<IconEye size={11} />
							</ActionIcon>
						) : null}
						{showCopy ? (
							<ButtonWithPopoverFeedback
								iconComponent={<IconCopy size={13} />}
								onClick={() =>
									navigator.clipboard.writeText(strValue)
								}
								feedbackMessage="Copied!"
								title="Copy"
							/>
						) : null}
					</div>
				) : null}
			</td>
		</tr>
	);
}

function DevTransposedTable({
	data,
	tableUniqueID,
	cellRenderers,
	truncate = true,
	fieldLabel = "field",
	valueLabel = "value",
	minWidth,
}) {
	const dispatch = useDispatch();
	const persistedSort = useSelector((state) =>
		tableUniqueID ? state.tableSorting[tableUniqueID]?.sort : null
	);
	const [sort, setSort] = useState(() => persistedSort?.[0] ?? null);
	const [fullValue, setFullValue] = useState(null);

	const sortedEntries = useMemo(() => {
		if (!data) return [];
		const entries = Object.entries(data);
		if (!sort) return entries;
		return [...entries].sort(([ka, va], [kb, vb]) => {
			const a = sort.id === "field" ? ka : String(va ?? "");
			const b = sort.id === "field" ? kb : String(vb ?? "");
			const cmp = compareSortValues(a, b);
			return sort.desc ? -cmp : cmp;
		});
	}, [data, sort]);

	const handleHeaderClick = (colId) => {
		const newSort =
			sort?.id === colId
				? { id: colId, desc: !sort.desc }
				: { id: colId, desc: false };
		setSort(newSort);
		if (tableUniqueID) {
			dispatch(setTableSort(tableUniqueID, [newSort]));
		}
	};

	if (!data) {
		return (
			<span style={{ color: "#aaa", fontSize: 12, fontStyle: "italic" }}>
				No data
			</span>
		);
	}

	const fieldSorted =
		sort?.id === "field" ? (sort.desc ? "desc" : "asc") : false;
	const valueSorted =
		sort?.id === "value" ? (sort.desc ? "desc" : "asc") : false;

	return (
		<>
			<table
				style={{
					borderCollapse: "collapse",
					border: CELL_BORDER,
					minWidth,
				}}
			>
				<thead>
					<tr>
						<th
							style={{
								padding: "4px 8px",
								textAlign: "left",
								fontWeight: 700,
								fontSize: 12,
								fontFamily: "monospace",
								color: "#444",
								backgroundColor: "#f5f5f5",
								border: CELL_BORDER,
								cursor: "pointer",
								userSelect: "none",
								whiteSpace: "nowrap",
								width: "1px",
							}}
							onClick={() => handleHeaderClick("field")}
						>
							<span
								style={{
									display: "flex",
									alignItems: "center",
									gap: 4,
								}}
							>
								{fieldLabel}
								<SortIcon sorted={fieldSorted} />
							</span>
						</th>
						<th
							style={{
								padding: "4px 8px",
								textAlign: "left",
								fontWeight: 700,
								fontSize: 12,
								fontFamily: "monospace",
								color: "#444",
								backgroundColor: "#f5f5f5",
								border: CELL_BORDER,
								cursor: "pointer",
								userSelect: "none",
							}}
							onClick={() => handleHeaderClick("value")}
						>
							<span
								style={{
									display: "flex",
									alignItems: "center",
									gap: 4,
								}}
							>
								{valueLabel}
								<SortIcon sorted={valueSorted} />
							</span>
						</th>
					</tr>
				</thead>
				<tbody>
					{sortedEntries.map(([key, value]) => {
						const customRenderer = cellRenderers?.[key];
						if (customRenderer) {
							return (
								<CustomRendererTransposedRow
									key={key}
									field={key}
									value={value}
									customRenderer={customRenderer}
									onShowFull={setFullValue}
								/>
							);
						}
						return (
							<TransposedRow
								key={key}
								field={key}
								value={value}
								truncate={truncate}
								onShowFull={setFullValue}
							/>
						);
					})}
				</tbody>
			</table>
			<FullValueModal
				fullValue={fullValue}
				onClose={() => setFullValue(null)}
				defaultTitle="Field value"
			/>
		</>
	);
}

export default DevTransposedTable;
